/**
 * **Money → Bookings** — the list (C19, FR-J11 item 9, ADR-0037 §6; Wave 19B,
 * T-5.60). A `ListPage` over the kit's `DataTable`: who booked, through which
 * flow, what they chose, the amount, the invoice and the status. Filters by
 * status and by flow, and a search over the client, the flow, the service and
 * the invoice number. A row opens the booking's record.
 *
 * It reads the newest 200 (`GET /bookings?limit=200`) and filters what it
 * holds, as every staff list does (C14 §10.6 item 8: no server paging). When
 * the firm has more, the description says how many are not shown.
 *
 * `booking:can_view` — the rail entry is gated on it, and the service refuses
 * without it.
 *
 * **Staff are told** (C25, ADR-0052 §7; Wave 27B, T-6.22). *Proof to check* —
 * a bank-QR booking waiting for the firm — is a status, a filter and a count:
 * the status filter's options carry the service's `meta.counts`, which are
 * exact past the 200 this list holds; a *Proof to check* button sets the
 * filter when any wait; and the filter **deep-links** (`?status=payment_review`,
 * where *Your day*'s card points). The bookings that wait are read on their own
 * as well (`?status=payment_review`) and merged in, so one older than the
 * newest 200 is never missing from the list that says it exists.
 */

import { Button, formatDateTime } from '@fiveox/ui'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

import {
  ExportButton,
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  StatusBadge,
  statusTone,
  useListTable,
} from '../../../components/page'
import { ApiError } from '../../../lib/api'
import {
  BOOKING_STATUSES,
  type BookingFlow,
  type BookingRow,
  type BookingStatus,
  booking,
  bookingCounts,
} from '../../../lib/api/booking'
import { Money } from '../../Billing/shared'

/** C25 — *Proof to check*, the status staff act on (ADR-0052 §7). */
export const TO_CHECK: BookingStatus = 'payment_review'

/** A `?status=` value the list knows, else none. */
export function statusParam(value: string | null): string {
  return value && (BOOKING_STATUSES as string[]).includes(value) ? value : ''
}

/** The newest 200 and the bookings that wait, as one list — each once, newest
 *  first. */
export function mergeRows(newest: BookingRow[], waiting: BookingRow[]): BookingRow[] {
  const seen = new Set(newest.map((row) => row.id))
  return [...newest, ...waiting.filter((row) => !seen.has(row.id))]
}

/** A status filter option's words — with the service's count when it has one. */
export function statusOption(
  status: BookingStatus,
  counts: Partial<Record<BookingStatus, number>>,
): { value: string; label: string } {
  const label = statusTone('booking', status).label
  const n = counts[status]
  return { value: status, label: typeof n === 'number' ? `${label} (${n})` : label }
}

export function Bookings() {
  const [params, setParams] = useSearchParams()
  const [rows, setRows] = useState<BookingRow[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [counts, setCounts] = useState<Partial<Record<BookingStatus, number>>>({})
  const [flows, setFlows] = useState<BookingFlow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    Promise.all([
      booking.bookings({ limit: 200 }),
      booking.bookings({ status: TO_CHECK, limit: 200 }).catch(() => null),
      booking.flows().catch(() => null),
    ])
      .then(([list, waiting, flowList]) => {
        if (!live) return
        setRows(mergeRows(list.data, waiting?.data ?? []))
        setTotal(typeof list.meta?.count === 'number' ? list.meta.count : list.data.length)
        setCounts(bookingCounts(list))
        if (flowList) setFlows(flowList.data)
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof ApiError ? err.message : 'The bookings could not be loaded.')
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [])

  const columns = useMemo<ListColumn<BookingRow>[]>(
    () => [
      {
        key: 'client',
        label: 'Client',
        width: 190,
        required: true,
        phone: true,
        sortField: 'client_name',
        render: (row) => (
          <span className="block min-w-0">
            <span className="block max-w-[12rem] truncate font-medium" title={row.client_name ?? ''}>
              {row.client_name ?? '—'}
            </span>
            {/* On a phone the flow and the amount ride under the name. */}
            <span className="text-muted-foreground block truncate text-xs sm:hidden">
              {row.flow_name ?? row.flow_slug}
              {row.amount ? ` · ₹${row.amount}` : ''}
            </span>
          </span>
        ),
        exportValue: (row) => row.client_name ?? '',
      },
      {
        key: 'status',
        label: 'Status',
        width: 130,
        phone: true,
        sortField: 'status',
        render: (row) => <StatusBadge kind="booking" status={row.status} />,
        exportValue: (row) => statusTone('booking', row.status).label,
      },
      {
        key: 'flow',
        label: 'Flow',
        width: 150,
        sortField: 'flow_name',
        render: (row) => (
          <span className="block max-w-[10rem] truncate">{row.flow_name ?? row.flow_slug}</span>
        ),
        exportValue: (row) => row.flow_name ?? row.flow_slug,
      },
      {
        key: 'choice',
        label: 'Service and plan',
        width: 180,
        render: (row) => (
          <span className="block max-w-[12rem] truncate">
            {row.service_name ? `${row.service_name}${row.plan_name ? ` · ${row.plan_name}` : ''}` : '—'}
          </span>
        ),
        exportValue: (row) => [row.service_name, row.plan_name].filter(Boolean).join(' · '),
      },
      {
        key: 'amount',
        label: 'Amount',
        width: 110,
        align: 'right',
        sortField: 'amount',
        render: (row) => <Money value={row.amount} />,
        exportValue: (row) => row.amount ?? '',
      },
      {
        key: 'invoice',
        label: 'Invoice',
        width: 120,
        sortField: 'invoice_number',
        render: (row) => <span className="text-muted-foreground">{row.invoice_number ?? '—'}</span>,
        exportValue: (row) => row.invoice_number ?? '',
      },
      {
        key: 'created_at',
        label: 'Started',
        width: 150,
        sortField: 'created_at',
        render: (row) => (
          <span className="text-muted-foreground whitespace-nowrap">
            {row.created_at ? formatDateTime(row.created_at) : '—'}
          </span>
        ),
        exportValue: (row) => row.created_at ?? '',
      },
    ],
    [],
  )

  const flowOptions = useMemo(() => {
    // The flows the firm has, and any flow a booking names that is gone from
    // the list (a renamed or unreadable one) — so no booking is unfilterable.
    const seen = new Map<string, string>()
    for (const f of flows) seen.set(f.id, f.name)
    for (const r of rows) if (!seen.has(r.flow_id)) seen.set(r.flow_id, r.flow_name ?? r.flow_slug)
    return [...seen].map(([value, label]) => ({ value, label }))
  }, [flows, rows])

  const list = useListTable(rows, {
    columns,
    search: (row) => [row.client_name, row.flow_name, row.flow_slug, row.service_name, row.plan_name, row.invoice_number],
    filters: [
      {
        key: 'status',
        label: 'Status',
        allLabel: 'Any status',
        options: BOOKING_STATUSES.map((value) => statusOption(value, counts)),
        match: (row, value) => row.status === value,
        // The deep link — *Your day*'s card opens the list on *Proof to check*.
        initial: statusParam(params.get('status')),
      },
      {
        key: 'flow',
        label: 'Flow',
        allLabel: 'Every flow',
        options: flowOptions,
        match: (row, value) => row.flow_id === value,
      },
    ],
    initialSort: { field: 'created_at', dir: 'desc' },
  })

  const more = total !== null && total > rows.length ? ` Showing the newest ${rows.length} of ${total}.` : ''

  // The status chip and the address agree, so the filtered list is a link:
  // whichever moved last wins — a link or *Back* moves the chip, the chip
  // rewrites the address (in place, so *Back* still leaves the page).
  const status = list.filters.find((f) => f.def.key === 'status')
  const chosen = status?.value ?? ''
  const addressed = statusParam(params.get('status'))
  const seen = useRef(addressed)
  useEffect(() => {
    if (addressed !== seen.current) {
      seen.current = addressed
      if (addressed !== chosen) status?.set(addressed)
      return
    }
    if (chosen === addressed) return
    seen.current = chosen
    const next = new URLSearchParams(params)
    if (chosen) next.set('status', chosen)
    else next.delete('status')
    setParams(next, { replace: true })
    // `status` is rebuilt every render; its value is what is read here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressed, chosen])

  const waiting = counts[TO_CHECK] ?? rows.filter((row) => row.status === TO_CHECK).length

  return (
    <ListPage
      title="Bookings"
      description={`Everyone who has booked through one of your booking flows, newest first.${more}`}
      loading={loading}
      error={error}
      isEmpty={list.isEmpty}
      empty={{
        title: 'No bookings yet',
        description:
          'A booking appears here the moment a visitor verifies in one of your live booking flows. Build one under Your website → Booking flows.',
      }}
      toolbar={
        <ListToolbar
          list={list}
          placeholder="Search by client, flow, service or invoice"
          actions={<ExportButton rows={list.sorted} columns={columns} filename="prodesk-bookings" />}
        >
          {waiting > 0 && chosen !== TO_CHECK && (
            <Button size="sm" variant="secondary" onClick={() => status?.set(TO_CHECK)} data-to-check="">
              Proof to check · {waiting}
            </Button>
          )}
        </ListToolbar>
      }
    >
      <ListTable list={list} rowKey={(row) => row.id} rowHref={(row) => `/bookings/${row.id}`} />
    </ListPage>
  )
}
